import posthog from 'posthog-js';
import { useEffect } from 'react';
import { useLocation } from '@remix-run/react';

/**
 * Public, client-side project key — `phc_` keys are designed to ship in the browser bundle.
 *
 * The "Deck" project (256733) in the g@deck.team organization. The previous key pointed at a
 * project this account cannot see, so nothing Deck sent was reachable; it was found and swapped
 * on 2026-09-05, before launch, so no useful history was lost.
 *
 * Read from the build env first, so a future move is a config change rather than a code edit: set
 * the repo variable `POSTHOG_KEY` and the deploy workflow passes it as `VITE_POSTHOG_KEY` beside
 * `APP_URL`. The literal is the fallback so a local build with no env still reports.
 */
export const POSTHOG_KEY = import.meta.env.VITE_POSTHOG_KEY || 'phc_pAmDtTpEaMj6qtnkifXY2CJWbUZdM3DDrHcTz9uaMMvm';

/**
 * First-party, through `app/routes/ingest.$.tsx`. Pointing the SDK straight at `eu.i.posthog.com`
 * loses every visitor running an ad blocker, which is the population most likely to be an early
 * technical user. `ui_host` is still the real one so "view in PostHog" links resolve.
 */
const POSTHOG_PROXY = '/ingest';
const POSTHOG_UI_HOST = 'https://eu.posthog.com';

let started = false;
let identified = false;

/**
 * Local development never reports. Our own laptops sent nine in ten of all error reports and the
 * heaviest recordings, which buried what real people do. Staging and production still report.
 */
function isLocalHost(): boolean {
  const host = window.location.hostname;
  return host === 'localhost' || host === '127.0.0.1' || host === '[::1]' || host.endsWith('.localhost');
}

/**
 * Initialise PostHog once, in the browser only, and only after hydration: from PostHogTracker's first
 * effect, or from the root ErrorBoundary when that is what rendered.
 *
 * Never before hydration. `posthog.init` inserts its remote config and exception scripts into
 * `<body>` straight away, ahead of the first script there, so React then hydrates a body two nodes
 * longer than the one it rendered on the server. That mismatch threw React errors #418 and #423 on
 * every page and made React throw away the server's markup and render the whole document again.
 */
export function initPostHog() {
  if (started || typeof window === 'undefined' || isLocalHost()) return;
  started = true;
  posthog.init(POSTHOG_KEY, {
    api_host: POSTHOG_PROXY,
    ui_host: POSTHOG_UI_HOST,
    capture_pageview: false, // captured manually on Remix SPA navigations (see PostHogTracker)
    capture_pageleave: true,
    autocapture: true,
    capture_exceptions: true, // Error Tracking: auto-send uncaught errors + unhandled promise rejections
    person_profiles: 'identified_only', // no person profile for anon traffic — keeps events/cost lean
    // Session replay records 1 in 4 sessions. That rate is a PostHog project setting (Replay, sampling),
    // not an option here: the project's remote config decides it for every client.
  });
}

export { posthog };

/**
 * Every capture in the app goes through here.
 *
 * An ad blocker that kills posthog-js leaves `posthog.capture` throwing, and an unguarded call
 * inside a click handler takes the click with it. Analytics must never be able to break the
 * product, so a failed send is swallowed.
 */
export function track(event: string, properties?: Record<string, unknown>) {
  if (!started) return;
  try {
    posthog.capture(event, properties);
  } catch {
    // Reporting is best effort. There is nothing useful to do with the failure.
  }
}

/** An error React caught before it reached window.onerror, for Error Tracking. */
export function reportException(error: unknown) {
  if (!started) return;
  try {
    posthog.captureException(error);
  } catch {
    // see `track`
  }
}

/** Properties that describe the person rather than one event, such as where they heard of Deck. */
export function setPersonProperties(properties: Record<string, unknown>) {
  if (!started) return;
  try {
    posthog.setPersonProperties(properties);
  } catch {
    // see `track`
  }
}

type TrackUser = { id?: string | null; email?: string | null } | null | undefined;

/**
 * Rendered once in the root layout. Captures a $pageview on every client-side route
 * change, and identifies (on login) / resets (on logout) the PostHog person.
 */
export function PostHogTracker({ user }: { user: TrackUser }) {
  const location = useLocation();

  // First, so the pageview below is sent by a started client. Effects run after hydration.
  useEffect(() => {
    initPostHog();
  }, []);

  useEffect(() => {
    if (typeof window === 'undefined') return;
    track('$pageview');
  }, [location.pathname, location.search]);

  useEffect(() => {
    if (typeof window === 'undefined' || !started) return;
    if (user?.id) {
      try {
        posthog.identify(user.id, user.email ? { email: user.email } : undefined);
      } catch {
        // see `track`
      }
      identified = true;
    } else if (identified) {
      // only reset on a real logout (identified -> anon), never on first anon mount
      try {
        posthog.reset();
      } catch {
        // see `track`
      }
      identified = false;
    }
  }, [user?.id, user?.email]);

  return null;
}
